import type { Event, EventHint } from '@sentry/react';

/** Patterns passés à `Sentry.init({ ignoreErrors })` + `shouldDropSentryEvent`. */
export const SENTRY_IGNORE_ERROR_PATTERNS: Array<string | RegExp> = [
  'Token has expired',
  /TOKEN_EXPIRED/i,
  'ChunkLoadError',
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /^Créneau \d+ complet$/i,
  /Créneau \d+ complet\b/,
];

export function collectSentryEventText(event: Event): string {
  const parts: string[] = [];
  if (typeof event.message === 'string') parts.push(event.message);
  if (event.logentry?.message) parts.push(event.logentry.message);
  for (const ex of event.exception?.values ?? []) {
    if (ex.type) parts.push(ex.type);
    if (ex.value) parts.push(ex.value);
  }
  return parts.join('\n');
}

export function isIgnoredSentryErrorMessage(text: string): boolean {
  if (!text) return false;
  for (const pattern of SENTRY_IGNORE_ERROR_PATTERNS) {
    if (typeof pattern === 'string') {
      if (text.includes(pattern)) return true;
    } else if (pattern.test(text)) {
      return true;
    }
  }
  return false;
}

function breadcrumbMentionsHeap(urlish: string): boolean {
  return /heap-api\.com/i.test(urlish);
}

/** « Failed to fetch » vers Heap analytics uniquement — pas nos API Rintintin. */
export function isHeapAnalyticsFailedFetch(event: Event): boolean {
  const text = collectSentryEventText(event);
  const looksLikeFailedFetch = /Failed to fetch/i.test(text);
  if (!looksLikeFailedFetch && !text.includes('NetworkError')) {
    return false;
  }

  if (breadcrumbMentionsHeap(text)) return true;
  if (event.request?.url && breadcrumbMentionsHeap(event.request.url)) return true;

  for (const crumb of event.breadcrumbs ?? []) {
    const data = crumb.data as Record<string, unknown> | undefined;
    const blob = [
      crumb.message,
      typeof data?.url === 'string' ? data.url : '',
      typeof data?.to === 'string' ? data.to : '',
      typeof data?.from === 'string' ? data.from : '',
    ].join(' ');
    if (breadcrumbMentionsHeap(blob)) return true;
  }

  return false;
}

export function shouldDropSentryEvent(event: Event, _hint?: EventHint): boolean {
  const text = collectSentryEventText(event);
  if (isIgnoredSentryErrorMessage(text)) return true;
  if (isHeapAnalyticsFailedFetch(event)) return true;
  return false;
}

export function createSentryBeforeSend(
  existing?: (event: Event, hint: EventHint) => Event | null
): (event: Event, hint: EventHint) => Event | null {
  return (event, hint) => {
    if (shouldDropSentryEvent(event, hint)) return null;
    if (existing) return existing(event, hint);
    return event;
  };
}
